import { gql, useQuery } from "@apollo/client";
import { Card, CircularProgress, Grid, Icon, Skeleton } from "@mui/material";
import coverCaza from "assets/images/cover_caza.png";
import logo from "assets/images/logo.png";
import logo_alt from "assets/images/logo_alt.png";
import PageLayout from "components/LayoutContainers/PageLayout";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import QRCode from "react-qr-code";
import { useParams } from "react-router-dom";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";

function ControlOcCoto() {
  const { url } = useParams();

  const { data, loading, error } = useQuery(
    gql`
      query getPermisoByURL($url: String!) {
        permisoOcCotoByUrl(url: $url) {
          id
          tipo
          url
          personaHabilitada
          dni
          domicilio
          fechaDesde
          fechaHasta
          valorPermisoTemporada
          importeAbonado
          nroPermiso
          anioPermiso
        }
      }
    `,
    {
      variables: { url },
    }
  );

  return (
    <PageLayout>
      <SoftBox display="flex" justifyContent="center" alignItems="center" height="100vh" p={3}>
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item xs={12} sm={4}>
            <Card>
              {loading ? (
                <SoftBox p={2}>
                  <SoftBox display="flex" justifyContent="center" alignItems="center">
                    <SoftBox
                      component="img"
                      src={logo}
                      alt="Gobierno de San Luis Logo"
                      width="300px"
                      p={3}
                    />
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    p={2}
                    textAlign="center"
                  >
                    <SoftTypography variant="h6">Buscando permiso</SoftTypography>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                    p={2}
                  >
                    <CircularProgress size={30} color="primary" />
                  </SoftBox>
                </SoftBox>
              ) : data?.permisoOcCotoByUrl &&
                dayjs(data?.permisoOcCotoByUrl?.fechaHasta).isAfter(dayjs().subtract(1, "day")) ? (
                <SoftBox id="certificado" p={3}>
                  <SoftBox position="relative">
                    <SoftBox
                      display="flex"
                      alignItems="top"
                      position="relative"
                      minHeight="18vh"
                      borderRadius="xl"
                      sx={{
                        backgroundImage: ({
                          functions: { rgba, linearGradient },
                          palette: { gradients },
                        }) =>
                          `${linearGradient(
                            rgba(gradients.primary.main, 0.6),
                            rgba(gradients.primary.state, 0.6)
                          )}, url(${coverCaza})`,
                        backgroundSize: "cover",
                        backgroundPosition: "50%",
                        overflow: "hidden",
                      }}
                    >
                      <Grid container justifyContent="center" p={4} mb={4} alignItems={"center"}>
                        <Grid item xs={12} sm={4}>
                          <SoftBox
                            component="img"
                            src={logo_alt}
                            alt="Gobierno de la Provincia de San Luis Logo"
                            width="100%"
                            p={3}
                          />
                        </Grid>
                      </Grid>
                    </SoftBox>
                    <Card
                      sx={{
                        backdropFilter: `saturate(200%) blur(30px)`,
                        backgroundColor: ({ functions: { rgba }, palette: { white } }) =>
                          rgba(white.main, 1),
                        boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
                        position: "relative",
                        mt: -8,
                        mx: 2,
                        py: 2,
                        px: 2,
                      }}
                    >
                      <SoftBox display="flex" justifyContent="space-between">
                        <SoftBox>
                          <SoftTypography variant="h6" fontWeight="light">
                            Número de permiso
                          </SoftTypography>
                          <SoftTypography variant="h6" fontWeight="bold">
                            {loading ? (
                              <Skeleton variant="text" width={100} />
                            ) : (
                              `${data?.permisoOcCotoByUrl?.anioPermiso} - ${data?.permisoOcCotoByUrl?.nroPermiso}`
                            )}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox textAlign="right">
                          <SoftTypography variant="h6" fontWeight="light">
                            Fecha de emisión
                          </SoftTypography>
                          <SoftTypography variant="h6" fontWeight="bold">
                            {loading ? (
                              <Skeleton variant="text" width={100} />
                            ) : (
                              dayjs(data?.permisoOcCotoByUrl?.fechaInicio).format("DD/MM/YYYY")
                            )}
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                          <SoftTypography variant="h5" fontWeight="bold" textAlign="center">
                            {loading ? (
                              <Skeleton variant="text" width={100} />
                            ) : (
                              `Permiso ${data?.permisoOcCotoByUrl?.tipo}`
                            )}
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <SoftTypography
                            variant="h6"
                            fontWeight="light"
                            textAlign={{
                              sm: "left",
                              xs: "center",
                            }}
                          >
                            El presente documento acredita que{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {makeNiceText(
                                `${data?.permisoOcCotoByUrl?.personaHabilitada}`,
                                "nombre"
                              )}
                            </span>{" "}
                            con DNI{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {formatMoneyPunto(data?.permisoOcCotoByUrl?.dni)}
                            </span>{" "}
                            le ha sido otorgado el permiso de{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {data?.permisoOcCotoByUrl?.tipo}
                            </span>{" "}
                            en la Provincia de San Luis
                            {data?.permisoOcCotoByUrl?.tipo === "Coto" ? (
                              <>
                                desde el día{" "}
                                <span style={{ fontWeight: "bold" }}>
                                  {dayjs(data?.permisoOcCotoByUrl?.fechaDesde).format("DD/MM/YYYY")}
                                </span>{" "}
                                hasta el día{" "}
                                <span style={{ fontWeight: "bold" }}>
                                  {dayjs(data?.permisoOcCotoByUrl?.fechaHasta).format("DD/MM/YYYY")}
                                </span>{" "}
                                inclusive
                              </>
                            ) : (
                              <>
                                por el año{" "}
                                <span style={{ fontWeight: "bold" }}>
                                  {data?.permisoOcCotoByUrl?.anioPermiso}
                                </span>
                              </>
                            )}
                            .
                            <br />
                            Este permiso solo es válido solo acompañado del DNI del titular.
                          </SoftTypography>
                        </Grid>
                        <Grid item xs={12}>
                          <SoftBox>
                            <SoftTypography variant="h6" fontWeight="bold">
                              Campos autorizados
                            </SoftTypography>
                            {data?.permisoOcCotoByUrl?.comprobantesPago
                              ?.filter(
                                (comprobante) =>
                                  comprobante.tipo === "Permiso de campo" &&
                                  comprobante.estado === "Aprobado"
                              )
                              .map((comprobante) => (
                                <SoftBox
                                  key={comprobante.id}
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <Icon>check</Icon>
                                  <SoftTypography variant="h6" fontWeight="light">
                                    {comprobante.nombre}
                                  </SoftTypography>
                                </SoftBox>
                              ))}
                          </SoftBox>
                        </Grid>
                      </Grid>
                      <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        mt={3}
                        alignItems="center"
                      >
                        <SoftBox
                          pl={2}
                          component="img"
                          src={logo}
                          alt="Gobierno de la Provincia de San Luis Logo"
                          width="160px"
                        />
                        {!loading ? (
                          <Card>
                            <SoftBox
                              p={2}
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                            >
                              <QRCode
                                size={90}
                                style={{ height: "auto", maxWidth: "90px", width: "90px" }}
                                value={`${window.location.origin}/control-oc-coto/${data?.permisoOcCotoByUrl?.url}`}
                                viewBox={`0 0 90 90`}
                              />
                            </SoftBox>
                          </Card>
                        ) : (
                          <Card>
                            <SoftBox
                              p={2}
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                            >
                              <Skeleton
                                variant="rectangular"
                                sx={{
                                  borderRadius: "10px",
                                }}
                                width={90}
                                height={90}
                              />
                            </SoftBox>
                          </Card>
                        )}
                      </SoftBox>
                      <SoftBox display="flex" justifyContent="center" mt={3} alignItems="center">
                        <SoftBox textAlign="center">
                          <SoftTypography variant="caption" fontWeight="light">
                            Powered by&nbsp;
                          </SoftTypography>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Gobierno de la Provincia de San Luis
                          </SoftTypography>
                        </SoftBox>
                      </SoftBox>
                    </Card>
                  </SoftBox>
                </SoftBox>
              ) : (
                <SoftBox p={2}>
                  <SoftBox display="flex" justifyContent="center" alignItems="center">
                    <SoftBox
                      component="img"
                      src={logo}
                      alt="Gobierno de San Luis Logo"
                      width="300px"
                      p={3}
                    />
                  </SoftBox>
                  <SoftBox display="flex" justifyContent="center" alignItems="center">
                    <Icon fontSize="large" color="error">
                      error
                    </Icon>
                  </SoftBox>
                  <SoftBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    p={2}
                    textAlign="center"
                  >
                    <SoftTypography variant="h6">
                      {data?.permisoOcCotoByUrl?.id ? "Permiso no válido" : "Permiso no encontrado"}
                    </SoftTypography>
                  </SoftBox>
                </SoftBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </PageLayout>
  );
}

export default ControlOcCoto;
